import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { Edit, School } from '@mui/icons-material';
import type { ChildSchoolProfileSummary, CourseCard, EnrollmentFailure } from '../../services/parentCourseService';

interface Props {
  childProfile: ChildSchoolProfileSummary;
  selectedCourses: CourseCard[];
  failures?: EnrollmentFailure[];
  submitting: boolean;
  onEditProfile: () => void;
  onConfirm: () => void;
  onBack: () => void;
}

const EnrollmentReviewStep: React.FC<Props> = ({
  childProfile,
  selectedCourses,
  failures,
  submitting,
  onEditProfile,
  onConfirm,
  onBack,
}) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review enrollment details
      </Typography>

      {/* Child profile summary */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <School fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>
              Student profile
            </Typography>
          </Box>
          <Button size="small" startIcon={<Edit fontSize="small" />} onClick={onEditProfile}>
            Edit
          </Button>
        </Box>
        <Typography variant="body2">
          <strong>Name:</strong> {childProfile.firstName} {childProfile.lastName}
        </Typography>
        {childProfile.gradeLevel && (
          <Typography variant="body2">
            <strong>Grade:</strong> {childProfile.gradeLevel}
          </Typography>
        )}
        {childProfile.schoolName && (
          <Typography variant="body2">
            <strong>School:</strong> {childProfile.schoolName}
          </Typography>
        )}
        {childProfile.councilName && (
          <Typography variant="body2">
            <strong>Council:</strong> {childProfile.councilName}
          </Typography>
        )}
        {childProfile.countryName && (
          <Typography variant="body2">
            <strong>Country:</strong> {childProfile.countryName}
          </Typography>
        )}
      </Box>

      {/* Selected courses */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Courses to enroll in ({selectedCourses.length})
      </Typography>
      <List dense disablePadding sx={{ mb: 3 }}>
        {selectedCourses.map((course) => (
          <React.Fragment key={course.courseId}>
            <ListItem disableGutters>
              <ListItemText
                primary={course.name}
                secondary={[course.courseType, course.curriculumLevel].filter(Boolean).join(' · ')}
              />
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
      </List>

      {/* Per-course failures (shown after a partial-success attempt) */}
      {failures && failures.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            The following courses could not be enrolled:
          </Typography>
          {failures.map((f) => (
            <Typography key={f.courseId} variant="body2">
              • Course {f.courseId}: {f.reason}
            </Typography>
          ))}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onBack} disabled={submitting}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={selectedCourses.length === 0 || submitting}
          size="large"
        >
          {submitting ? 'Enrolling…' : 'Confirm enrollment'}
        </Button>
      </Box>
    </Box>
  );
};

export default EnrollmentReviewStep;
